import React, { useState, useEffect, useCallback } from 'react';
import { invAuditService } from '../../services/inventoryService';
import { card, pageWrap, btn, inputStyle, label, fieldGroup, modal, modalBox,
  sectionTitle, errorBox, emptyState, fmtQty, fmtCost, fmtDate, friendlyErr } from './inventoryStyles';

const StatusBadge = ({ status }) => {
  const s = status === 'completed'
    ? { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', label: 'Completed' }
    : { bg: '#fffbeb', color: '#d97706', border: '#fde68a', label: 'In Progress' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
      background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
};

const StockAudit = ({ restaurant }) => {
  const [audits, setAudits]       = useState([]);
  const [activeAudit, setActive]  = useState(null); // audit with items
  const [loading, setLoading]     = useState(true);
  const [showNew, setShowNew]     = useState(false);
  const [newName, setNewName]     = useState('');
  const [creating, setCreating]   = useState(false);
  const [completing, setCompleting] = useState(false);
  const [saving, setSaving]       = useState({});
  const [error, setError]         = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await invAuditService.getAll(restaurantId);
    setAudits(data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const openAudit = async (audit) => {
    const { data } = await invAuditService.getWithItems(audit.id);
    setActive(data);
  };

  const createAudit = async () => {
    if (!newName.trim()) { setError('Enter an audit name.'); return; }
    setCreating(true); setError('');
    const { data, error: e } = await invAuditService.create(restaurantId, newName);
    setCreating(false);
    if (e) { setError(friendlyErr(e)); return; }
    setShowNew(false); setNewName('');
    load();
    if (data) openAudit(data);
  };

  const updateItem = async (auditItemId, physicalStock, reason) => {
    setSaving(s => ({ ...s, [auditItemId]: true }));
    await invAuditService.updateAuditItem(auditItemId, parseFloat(physicalStock), reason);
    setSaving(s => ({ ...s, [auditItemId]: false }));
    // Update local state
    setActive(prev => ({
      ...prev,
      items: prev.items.map(i => i.id === auditItemId ? { ...i, physical_stock: parseFloat(physicalStock), reason } : i),
    }));
  };

  const completeAudit = async () => {
    if (!activeAudit) return;
    const entered = activeAudit.items.filter(i => i.physical_stock !== null && i.physical_stock !== undefined);
    if (entered.length === 0) { setError('Enter at least one physical count.'); return; }
    setCompleting(true); setError('');
    const { error: e } = await invAuditService.complete(restaurantId, activeAudit.id);
    setCompleting(false);
    if (e) { setError(friendlyErr(e)); return; }
    setActive(null); load();
  };

  const totalVariance = activeAudit?.items?.reduce((s, i) => {
    if (i.physical_stock === null || i.physical_stock === undefined) return s;
    return s + (i.physical_stock - i.system_stock) * (i.inventory_item?.avg_unit_cost || 0);
  }, 0) || 0;

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>

        {/* Header */}
        {!activeAudit && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Stock Audit</h2>
                <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>Physical count to reconcile system stock</p>
              </div>
              <button style={btn('primary')} onClick={() => { setShowNew(true); setNewName(`Audit ${new Date().toLocaleDateString('en-IN')}`); setError(''); }}>
                + New Audit
              </button>
            </div>

            <div style={{ ...card, overflow: 'hidden' }}>
              {loading ? (
                <div style={emptyState}>Loading…</div>
              ) : audits.length === 0 ? (
                <div style={emptyState}>
                  <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No audits yet</div>
                  <div style={{ color: '#aaa', fontSize: '0.88rem' }}>Create a stock audit to compare system stock against physical counts.</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                      {['Date', 'Name', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {audits.map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 14px', color: '#888', fontSize: '0.82rem' }}>{fmtDate(a.audit_date)}</td>
                        <td style={{ padding: '12px 14px', fontWeight: '600', color: '#333' }}>{a.name}</td>
                        <td style={{ padding: '12px 14px' }}><StatusBadge status={a.status} /></td>
                        <td style={{ padding: '12px 14px' }}>
                          {a.status === 'in_progress' && (
                            <button style={{ ...btn('primary'), padding: '5px 14px', fontSize: '0.82rem' }} onClick={() => openAudit(a)}>
                              Continue
                            </button>
                          )}
                          {a.status === 'completed' && (
                            <button style={{ ...btn('outline'), padding: '5px 14px', fontSize: '0.82rem' }} onClick={() => openAudit(a)}>
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Active audit count sheet */}
        {activeAudit && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <button style={{ ...btn('outline'), padding: '5px 12px', fontSize: '0.82rem', marginBottom: '8px' }} onClick={() => setActive(null)}>
                  ← Back to list
                </button>
                <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: '#111' }}>{activeAudit.name}</h2>
                <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>
                  {fmtDate(activeAudit.audit_date)} · <StatusBadge status={activeAudit.status} />
                  {totalVariance !== 0 && (
                    <span style={{ marginLeft: '12px', color: totalVariance > 0 ? '#16a34a' : '#dc2626', fontWeight: '700' }}>
                      Variance: {totalVariance > 0 ? '+' : ''}{fmtCost(totalVariance)}
                    </span>
                  )}
                </p>
              </div>
              {activeAudit.status === 'in_progress' && (
                <button style={btn('primary', completing)} onClick={completeAudit} disabled={completing}>
                  {completing ? 'Processing…' : 'Complete Audit'}
                </button>
              )}
            </div>

            {error && <div style={errorBox}>{error}</div>}

            <div style={{ ...card, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                    {['Item', 'System Stock', 'Physical Count', 'Variance', 'Reason'].map(h => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(activeAudit.items || []).map(item => {
                    const variance = item.physical_stock !== null && item.physical_stock !== undefined
                      ? item.physical_stock - item.system_stock : null;
                    const isEditable = activeAudit.status === 'in_progress';
                    return (
                      <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 14px', fontWeight: '600', color: '#333' }}>
                          {item.inventory_item?.name}
                          <div style={{ fontSize: '0.78rem', color: '#aaa', fontWeight: '400' }}>
                            {item.inventory_item?.base_unit?.short_code || ''}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px', color: '#555' }}>{fmtQty(item.system_stock)}</td>
                        <td style={{ padding: '12px 14px' }}>
                          {isEditable ? (
                            <input
                              type="number" min="0" step="0.001"
                              style={{ ...inputStyle(), width: '100px', padding: '6px 10px' }}
                              defaultValue={item.physical_stock ?? ''}
                              onBlur={e => {
                                const val = e.target.value;
                                if (val !== '') updateItem(item.id, val, item.reason);
                              }}
                            />
                          ) : (
                            <span style={{ fontWeight: '700' }}>{item.physical_stock !== null ? fmtQty(item.physical_stock) : '—'}</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: '700', color: variance === null ? '#aaa' : variance > 0 ? '#16a34a' : variance < 0 ? '#dc2626' : '#888' }}>
                          {variance === null ? '—' : `${variance > 0 ? '+' : ''}${fmtQty(variance)}`}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {isEditable ? (
                            <input
                              style={{ ...inputStyle(), width: '160px', padding: '6px 10px' }}
                              defaultValue={item.reason || ''}
                              placeholder="Optional reason…"
                              onBlur={e => {
                                if (item.physical_stock !== null) updateItem(item.id, item.physical_stock, e.target.value);
                              }}
                            />
                          ) : (
                            <span style={{ color: '#888', fontSize: '0.82rem' }}>{item.reason || '—'}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* New Audit Modal */}
      {showNew && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowNew(false); }}>
          <div style={{ ...modalBox, maxWidth: '420px' }}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>New Stock Audit</h3>
            {error && <div style={errorBox}>{error}</div>}
            <div style={fieldGroup}>
              <label style={label}>Audit Name *</label>
              <input style={inputStyle(!newName)} value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Weekly count — April" autoFocus />
            </div>
            <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '20px' }}>
              A snapshot of current system stock will be taken for all active items. You then enter physical counts item by item.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={btn('outline')} onClick={() => setShowNew(false)}>Cancel</button>
              <button style={btn('primary', creating)} onClick={createAudit} disabled={creating}>{creating ? 'Creating…' : 'Create Audit'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockAudit;
